import React, { useCallback, useRef, useState } from 'react'
import { Icon } from '@/components/ui/icon'
import { AudioWaveform } from './AudioWaveform'
import { formatDuration } from './audio-peaks'
import { useAudioPeaks } from './useAudioPeaks'

interface AudioFilePlayerProps {
  audioUrl: string
  filename?: string
  /** The file's size when known; a large file skips the waveform. */
  sizeBytes?: number
}

/**
 * Loaded AudioFile body: the whole-file waveform (click to seek), filename and
 * duration, and native controls. Controls and waveform carry `nodrag` so
 * scrubbing never drags the node.
 */
export function AudioFilePlayer({ audioUrl, filename, sizeBytes }: AudioFilePlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const { status, peaks, duration: decodedDuration } = useAudioPeaks(audioUrl, sizeBytes)
  const [elementDuration, setElementDuration] = useState<number | null>(null)
  const [progress, setProgress] = useState(0)

  // The element reports duration even when decoding fails (e.g. no CORS).
  const duration = decodedDuration ?? elementDuration

  const handleLoadedMetadata = useCallback((e: React.SyntheticEvent<HTMLAudioElement>) => {
    const value = e.currentTarget.duration
    setElementDuration(Number.isFinite(value) ? value : null)
  }, [])

  const handleTimeUpdate = useCallback((e: React.SyntheticEvent<HTMLAudioElement>) => {
    const { currentTime, duration: total } = e.currentTarget
    setProgress(total > 0 && Number.isFinite(total) ? currentTime / total : 0)
  }, [])

  const handleSeek = useCallback((fraction: number) => {
    const audio = audioRef.current
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return
    audio.currentTime = fraction * audio.duration
    setProgress(fraction)
  }, [])

  return (
    <div className="flex flex-1 flex-col w-full gap-2 rounded-[10px] bg-muted/40 p-3">
      <div className="flex items-center gap-2 min-w-0 pr-28">
        <Icon name="music" className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="truncate text-xs font-medium text-foreground" title={filename}>
          {filename || 'Audio'}
        </span>
      </div>
      <AudioWaveform
        className="flex-1 min-h-16"
        peaks={peaks}
        status={status}
        progress={progress}
        onSeek={handleSeek}
      />
      <div className="flex items-center justify-between text-[10px] tabular-nums text-muted-foreground">
        <span>{status === 'failed' ? 'Waveform unavailable' : status === 'loading' ? 'Reading waveform...' : ''}</span>
        <span data-testid="audio-duration">{formatDuration(duration)}</span>
      </div>
      <audio
        ref={audioRef}
        src={audioUrl}
        controls
        preload="metadata"
        className="nodrag nopan w-full h-8"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
      />
    </div>
  )
}
